import L from 'leaflet';
import { formatDateTime } from '../utils/formatters';
import { escapeHtml } from '../utils/sanitize';
import { getAuthHeaders } from '../pages/assets';

export class OpmAssetMap extends HTMLElement {
	private map: L.Map | null = null;
	private markers: L.Marker[] = [];
	private _assets: any[] = [];
	private _sites: any[] = [];
	private _customers: any[] = [];
	private _deviceModels: any[] = [];
	private _tickets: any[] = [];
	private ticketCache: Map<string, { createdAt: string; ticketNumber?: string; status?: string }> = new Map();

	private resizeObserver: ResizeObserver | null = null;
	private hasValidSize = false;
	private currentLatLngs: L.LatLngExpression[] = [];
	private hasFitBounds = false;
	private isUserInteracting = false;

	constructor() {
		super();
		this.style.display = 'block';
		this.style.width = '100%';
		this.style.height = '100%';
	}

	connectedCallback() {
		this.initMap();
		this.setupResizeObserver();
		this.fetchTicketsIfNeeded();
	}

	disconnectedCallback() {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		}
		if (this.map) {
			this.map.remove();
			this.map = null;
		}
	}

	private fetchTicketsIfNeeded() {
		if (this._tickets && this._tickets.length > 0) return;
		try {
			const headers = getAuthHeaders();
			fetch('/api/v1/tickets?pageSize=500', { headers })
				.then(res => res.ok ? res.json() : null)
				.then(data => {
					if (data) {
						const items = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
						if (items.length > 0) {
							this._tickets = items;
							this.updateMap();
						}
					}
				})
				.catch(err => console.error('Failed to fetch tickets for asset map', err));
		} catch (e) {
			// ignore auth header error in unauthenticated preview
		}
	}

	private setupResizeObserver() {
		this.resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				const { width, height } = entry.contentRect;
				if (width > 0 && height > 0) {
					const wasInvalid = !this.hasValidSize;
					this.hasValidSize = true;
					
					if (this.map) {
						this.map.invalidateSize();
						if (wasInvalid) {
							this.fitMapBounds();
						}
					}
				} else {
					this.hasValidSize = false;
				}
			}
		});
		this.resizeObserver.observe(this);
	}

	get assets() {
		return this._assets;
	}

	set assets(val: any[]) {
		this._assets = Array.isArray(val) ? val : [];
		this.updateMap();
	}

	get sites() {
		return this._sites;
	}

	set sites(val: any[]) {
		this._sites = Array.isArray(val) ? val : [];
		this.updateMap();
	}

	get customers() {
		return this._customers;
	}

	set customers(val: any[]) {
		this._customers = Array.isArray(val) ? val : [];
		this.updateMap();
	}

	get deviceModels() {
		return this._deviceModels;
	}

	set deviceModels(val: any[]) {
		this._deviceModels = Array.isArray(val) ? val : [];
		this.updateMap();
	}

	get tickets() {
		return this._tickets;
	}

	set tickets(val: any[]) {
		this._tickets = Array.isArray(val) ? val : [];
		this.updateMap();
	}

	private initMap() {
		if (this.map) return;

		// Create a container element inside the custom component
		const mapDiv = document.createElement('div');
		mapDiv.style.width = '100%';
		mapDiv.style.height = '100%';
		mapDiv.style.position = 'relative';
		mapDiv.style.zIndex = '1';
		this.appendChild(mapDiv);

		// Initialize Leaflet Map
		this.map = L.map(mapDiv, {
			zoomControl: true,
			minZoom: 2,
		});

		// Add OpenStreetMap Tile Layer
		L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
			attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
		}).addTo(this.map);

		this.map.on('popupclose', () => {
			this.isUserInteracting = false;
		});

		// Listen to popup open to attach event handlers on dynamic buttons inside the map popup & fetch recent tickets
		this.map.on('popupopen', (e) => {
			this.isUserInteracting = true;
			const popupNode = e.popup.getElement();
			if (!popupNode) return;

			popupNode.querySelectorAll('[data-action]').forEach(btn => {
				btn.addEventListener('click', (event) => {
					event.preventDefault();
					const action = btn.getAttribute('data-action');
					const id = btn.getAttribute('data-id');
					
					// Dispatch dynamic custom event bubbling up to the parent page controller
					this.dispatchEvent(new CustomEvent('asset-map-action', {
						detail: { action, id },
						bubbles: true,
						composed: true
					}));
				});
			});

			// Asynchronously fetch most recent ticket if not cached
			const ticketContainer = popupNode.querySelector('[data-recent-ticket-asset-id]') as HTMLElement | null;
			if (ticketContainer) {
				const assetId = ticketContainer.getAttribute('data-recent-ticket-asset-id');
				if (assetId && !this.ticketCache.has(assetId)) {
					const headers = getAuthHeaders();
					fetch(`/api/v1/tickets?assetId=${assetId}&page=1&pageSize=10`, { headers })
						.then(res => res.ok ? res.json() : null)
						.then(data => {
							const items = data && Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
							if (items.length > 0) {
								// Sort by createdAt descending
								const sorted = items.slice().sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
								const activeTicket = sorted.find((t: any) => t.status === 'open' || t.status === 'assigned' || t.status === 'in_progress' || t.status === 'on_hold' || t.status === 'pending' || t.status === 'awaiting_parts') || sorted[0];
								this.ticketCache.set(assetId, { createdAt: activeTicket.createdAt, ticketNumber: activeTicket.ticketNumber, status: activeTicket.status });
								
								const dateStr = formatDateTime(activeTicket.createdAt);
								const ticketNumStr = activeTicket.ticketNumber ? ` (${escapeHtml(activeTicket.ticketNumber)})` : '';
								const isActive = activeTicket.status && activeTicket.status !== 'resolved' && activeTicket.status !== 'closed' && activeTicket.status !== 'cancelled';
								const statusStr = activeTicket.status ? ` · <span style="font-weight:600; text-transform:uppercase; font-size:0.7rem; color:${isActive ? '#f57c00' : '#637381'};">${escapeHtml(activeTicket.status)}</span>` : '';
								
								ticketContainer.innerHTML = `<strong>Recent Ticket:</strong> ${escapeHtml(dateStr)}${ticketNumStr}${statusStr}`;
							} else {
								ticketContainer.innerHTML = `<strong>Recent Ticket:</strong> None`;
							}
						})
						.catch(() => {
							ticketContainer.innerHTML = `<strong>Recent Ticket:</strong> None`;
						});
				}
			}
		});

		this.updateMap();
	}

	private updateMap() {
		if (!this.map) return;

		// Remove old markers
		this.markers.forEach(m => m.remove());
		this.markers = [];

		const oldLatLngsStr = JSON.stringify(this.currentLatLngs);
		this.currentLatLngs = [];
		const activeLatLngs: [number, number][] = [];
		const siteMap = new Map(this._sites.map(s => [s.id, s]));
		const customerMap = new Map<string, string>();
		(this._customers || []).forEach((c: any) => {
			if (c && c.id && c.name) {
				customerMap.set(c.id, c.name);
			}
		});

		const modelMap = new Map(this._deviceModels.map(m => [m.id, `${m.vendor || ''} ${m.modelName || ''}`.trim()]));

		// Build set of assets with active tickets (unresolved tickets)
		const activeTicketAssetIds = new Set<string>();
		const ticketMap = new Map<string, { createdAt: string; ticketNumber?: string; status?: string }>();
		(this._tickets || []).forEach((t: any) => {
			const isUnresolved = t.status && t.status !== 'resolved' && t.status !== 'closed' && t.status !== 'cancelled';
			if (t.assetId) {
				if (isUnresolved) {
					activeTicketAssetIds.add(t.assetId);
				}
				if (!ticketMap.has(t.assetId) || isUnresolved) {
					ticketMap.set(t.assetId, { createdAt: t.createdAt, ticketNumber: t.ticketNumber, status: t.status });
				}
			}
		});

		// Include cached tickets for active ticket detection
		this.ticketCache.forEach((info, assetId) => {
			if (info.status && info.status !== 'resolved' && info.status !== 'closed' && info.status !== 'cancelled') {
				activeTicketAssetIds.add(assetId);
			}
		});

		// Track location coordinate counts to handle overlapping marker offsets
		const coordUsageCount = new Map<string, number>();

		this._assets.forEach(asset => {
			const site = siteMap.get(asset.siteId);
			if (!site || typeof site.latitude !== 'number' || typeof site.longitude !== 'number') return;

			let lat = site.latitude;
			let lng = site.longitude;

			// Add a tiny random jitter for markers sharing identical coordinates
			const coordKey = `${lat.toFixed(6)},${lng.toFixed(6)}`;
			const count = coordUsageCount.get(coordKey) || 0;
			if (count > 0) {
				lat += (Math.random() - 0.5) * 0.00018;
				lng += (Math.random() - 0.5) * 0.00018;
			}
			coordUsageCount.set(coordKey, count + 1);

			const hasActiveTicket = activeTicketAssetIds.has(asset.id);
			if (hasActiveTicket) {
				activeLatLngs.push([lat, lng]);
			}
			this.currentLatLngs.push([lat, lng]);

			// Customer name & machine model resolution
			const customerName = asset.customerName || asset.customer_name || asset.customer?.name || customerMap.get(asset.customerId) || '—';
			const machineModel = [asset.vendor, asset.modelName].filter(Boolean).join(' ') || modelMap.get(asset.modelId) || '—';
			const cachedTicket = this.ticketCache.get(asset.id) || ticketMap.get(asset.id);

			let recentTicketHtml = 'None';
			if (cachedTicket) {
				const statusTag = cachedTicket.status ? ` · <span style="font-weight:600; text-transform:uppercase; font-size:0.7rem; color:${hasActiveTicket ? '#f57c00' : '#637381'};">${escapeHtml(cachedTicket.status)}</span>` : '';
				recentTicketHtml = `${escapeHtml(formatDateTime(cachedTicket.createdAt))}${cachedTicket.ticketNumber ? ` (${escapeHtml(cachedTicket.ticketNumber)})` : ''}${statusTag}`;
			}

			// Pin Color Rule: Orange (#f57c00) if active ticket present, otherwise Green (#107c41)
			const color = hasActiveTicket ? '#f57c00' : '#107c41';

			// Generate premium Shopify Polaris-styled HTML popup layout
			const popupHtml = `
				<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; width: 280px; max-width: 100%; box-sizing: border-box; padding: 2px; word-wrap: break-word; overflow-wrap: break-word; white-space: normal;">
					<div style="font-size: 0.875rem; font-weight: 700; margin-bottom: 4px; color: var(--p-color-text, #202223); word-break: break-all;">
						SN: ${escapeHtml(asset.serialNumber)}
					</div>
					<div style="font-size: 0.75rem; color: var(--p-color-text-subdued, #6d7175); margin-bottom: 3px; word-break: break-word;">
						<strong>Customer:</strong> ${escapeHtml(customerName)}
					</div>
					<div style="font-size: 0.75rem; color: var(--p-color-text-subdued, #6d7175); margin-bottom: 3px; word-break: break-word;">
						<strong>Machine:</strong> ${escapeHtml(machineModel)}
					</div>
					<div style="font-size: 0.75rem; color: var(--p-color-text-subdued, #6d7175); margin-bottom: 3px; word-break: break-word;">
						<strong>Site:</strong> ${escapeHtml(site.name)}
					</div>
					<div data-recent-ticket-asset-id="${escapeHtml(asset.id)}" style="font-size: 0.75rem; color: var(--p-color-text-subdued, #6d7175); margin-bottom: 6px; word-break: break-word;">
						<strong>Recent Ticket:</strong> ${recentTicketHtml}
					</div>
					<div style="margin-bottom: 8px; display: flex; gap: 4px; flex-wrap: wrap;">
						${hasActiveTicket ? '<s-badge tone="warning">Active Ticket</s-badge>' : ''}
						<s-badge tone="${asset.status === 'error' ? 'critical' : 'success'}">${escapeHtml(asset.status)}</s-badge>
					</div>
					<div style="display: flex; gap: 6px; flex-wrap: wrap;">
						<s-button data-action="view-history" data-id="${escapeHtml(asset.id)}" variant="primary" size="slim">View history</s-button>
						<s-button data-action="view-readings" data-id="${escapeHtml(asset.id)}" size="slim">Readings</s-button>
						<s-button data-action="edit-asset" data-id="${escapeHtml(asset.id)}" size="slim">Edit</s-button>
					</div>
				</div>
			`;

			const markerIcon = L.divIcon({
				html: `
					<svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="filter: drop-shadow(0px 2px 4px rgba(0,0,0,0.35));">
						<path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2Z" fill="${color}" stroke="#FFFFFF" stroke-width="1.5"/>
						<circle cx="12" cy="9" r="3" fill="#FFFFFF"/>
					</svg>
				`,
				className: 'opm-custom-marker',
				iconSize: [28, 28],
				iconAnchor: [14, 28],
				popupAnchor: [0, -28]
			});

			const marker = L.marker([lat, lng], { icon: markerIcon })
				.bindPopup(popupHtml, { minWidth: 260, maxWidth: 320 })
				.addTo(this.map!);

			this.markers.push(marker);
		});

		if (activeLatLngs.length > 0) {
			this.currentLatLngs = activeLatLngs;
		}

		const newLatLngsStr = JSON.stringify(this.currentLatLngs);
		const coordinatesChanged = oldLatLngsStr !== newLatLngsStr;

		if (coordinatesChanged || !this.hasFitBounds) {
			this.fitMapBounds();
			this.hasFitBounds = true;
		}
	}

	private fitMapBounds() {
		if (!this.map || !this.hasValidSize) return;
		if (this.isUserInteracting && this.hasFitBounds) return;

		// Dynamic Bounds calculations
		if (this.currentLatLngs.length > 0) {
			if (this.currentLatLngs.length === 1) {
				// Center on single marker with normal zoom
				this.map.setView(this.currentLatLngs[0]!, 14);
			} else {
				// Zoom out and adjust view bounds to fit all markers
				const bounds = L.latLngBounds(this.currentLatLngs);
				this.map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
			}
		} else {
			// Center at default position: Kathmandu, Nepal
			this.map.setView([27.7172, 85.3240], 13);
		}
	}
}

if (!customElements.get('opm-asset-map')) {
	customElements.define('opm-asset-map', OpmAssetMap);
}
